import { Injectable, signal, computed } from '@angular/core';

export interface CookieConsent {
  essentials: boolean;
  analytics: boolean;
}

export interface CookieConsentOptions {
  expires?: number; // days
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
}

@Injectable({
  providedIn: 'root',
})
export class CookieConsentService {
  private readonly COOKIE_NAME = 'doktar_cookie_consent';
  private readonly DEFAULT_OPTIONS: Required<CookieConsentOptions> = {
    expires: 365,
    path: '/',
    domain: '',
    secure: true,
    sameSite: 'Lax',
  };

  // Signals for reactive state management
  private readonly _consent = signal<CookieConsent | null>(null);
  private readonly _isBannerVisible = signal<boolean>(false);

  // Computed values
  readonly consent = this._consent.asReadonly();
  readonly isBannerVisible = this._isBannerVisible.asReadonly();
  readonly hasConsent = computed(() => this._consent() !== null);

  constructor() {
    this.initializeConsent();
  }

  /**
   * Initialize consent from cookies on service creation
   */
  private initializeConsent(): void {
    const storedConsent = this.getStoredConsent();
    if (storedConsent) {
      this._consent.set(storedConsent);
    } else {
      this._isBannerVisible.set(true);
    }
  }

  /**
   * Set consent for both essential and analytics cookies
   */
  setConsent(consent: CookieConsent, options?: CookieConsentOptions): void {
    const mergedOptions = { ...this.DEFAULT_OPTIONS, ...options };

    // Store consent in signal
    this._consent.set(consent);
    this._isBannerVisible.set(false);

    // Store in cookie
    const cookieValue = JSON.stringify(consent);
    this.setCookie(this.COOKIE_NAME, cookieValue, mergedOptions);
  }

  /**
   * Set consent for specific cookie type
   */
  setConsentForType(type: keyof CookieConsent, value: boolean, options?: CookieConsentOptions): void {
    const currentConsent = this._consent() || { essentials: false, analytics: false };
    const updatedConsent = { ...currentConsent, [type]: value };
    this.setConsent(updatedConsent, options);
  }

  /**
   * Get current consent for specific type
   */
  getConsentForType(type: keyof CookieConsent): boolean {
    const consent = this._consent();
    return consent?.[type] ?? false;
  }

  /**
   * Check if user has given consent for analytics
   */
  hasAnalyticsConsent(): boolean {
    return this.getConsentForType('analytics');
  }

  /**
   * Check if user has given consent for essentials
   */
  hasEssentialsConsent(): boolean {
    return this.getConsentForType('essentials');
  }

  /**
   * Show the consent banner
   */
  showBanner(): void {
    this._isBannerVisible.set(true);
  }

  /**
   * Hide the consent banner
   */
  hideBanner(): void {
    this._isBannerVisible.set(false);
  }

  /**
   * Clear all consent data
   */
  clearConsent(): void {
    this._consent.set(null);
    this._isBannerVisible.set(true);
    this.deleteCookie(this.COOKIE_NAME);
  }

  /**
   * Get stored consent from cookie
   */
  private getStoredConsent(): CookieConsent | null {
    const cookieValue = this.getCookie(this.COOKIE_NAME);
    if (!cookieValue) return null;

    try {
      return JSON.parse(cookieValue) as CookieConsent;
    } catch {
      // Invalid JSON, clear the cookie
      this.deleteCookie(this.COOKIE_NAME);
      return null;
    }
  }

  /**
   * Set a cookie with proper encoding and options
   */
  private setCookie(name: string, value: string, options: Required<CookieConsentOptions>): void {
    const expires = new Date();
    expires.setTime(expires.getTime() + options.expires * 24 * 60 * 60 * 1000);

    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=${options.path}`;

    if (options.domain) {
      cookieString += `; domain=${options.domain}`;
    }

    if (options.secure) {
      cookieString += '; Secure';
    }

    if (options.sameSite) {
      cookieString += `; SameSite=${options.sameSite}`;
    }

    document.cookie = cookieString;
  }

  /**
   * Get a cookie value by name
   */
  private getCookie(name: string): string | null {
    const nameEQ = encodeURIComponent(name) + '=';
    const cookies = document.cookie.split(';');

    for (const cookie of cookies) {
      const trimmedCookie = cookie.trim();
      if (trimmedCookie.indexOf(nameEQ) === 0) {
        return decodeURIComponent(trimmedCookie.substring(nameEQ.length));
      }
    }

    return null;
  }

  /**
   * Delete a cookie
   */
  private deleteCookie(name: string): void {
    document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }

  // Legacy methods for backward compatibility
  /**
   * @deprecated Use setConsent() instead
   */
  setLegacyConsent(consent: boolean): void {
    const consentData: CookieConsent = {
      essentials: true, // Essentials are always required
      analytics: consent,
    };
    this.setConsent(consentData);
  }

  /**
   * @deprecated Use getConsentForType('analytics') instead
   */
  getLegacyConsent(): boolean | null {
    const consent = this._consent();
    return consent ? consent.analytics : null;
  }
}
